import * as Tooltip from '@radix-ui/react-tooltip';
import { useHoverFetch } from '../../hooks/useHoverFetch';
import './StatusBadge.css';

const STYLES: Record<string, string> = {
  draft: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
  images: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  generated: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  packaged: 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300',
  bound: 'bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
  scheduled: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  published: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  failed: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300',
};

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-full rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div className="h-full bg-blue-500 dark:bg-blue-400 transition-all duration-300" data-progress={pct} />
    </div>
  );
}

export function StatusBadge({ taleId, stage }: { taleId: string; stage: string }) {
  const { data, loading, onOpen } = useHoverFetch<any>(`/api/tales/${taleId}/stats`);
  const cls = STYLES[stage] ?? STYLES.draft;

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root onOpenChange={(open) => open && onOpen()}>
        <Tooltip.Trigger asChild>
          <span className={`px-2 py-0.5 rounded-md text-xs cursor-help ${cls}`}>
            {stage}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content 
          side="top" 
          className="w-[320px] p-3 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-lg border border-slate-200 dark:border-slate-700 text-xs space-y-3"
        >
          {loading && <div className="animate-pulse text-slate-500 dark:text-slate-400">Loading…</div>}
          {!loading && data && (
            <>
              {/* Images */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-medium">Images</span>
                  <span>{data.images.done}/{data.images.total}</span>
                </div>
                <ProgressBar value={data.images.done} max={data.images.total} />
                <div className="mt-1 text-slate-500 dark:text-slate-400">
                  queued {data.images.queued} • generating {data.images.generating} • failed {data.images.failed}
                </div>
              </div>

              {/* Storyboard */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="font-medium">Storyboard</div>
                  <div className="text-slate-500 dark:text-slate-400">{data.storyboard.frames} frame(s)</div>
                </div>
                <div>
                  <div className="font-medium">Audio</div>
                  <div className="text-slate-500 dark:text-slate-400">
                    {data.storyboard.hasNarration ? 'Narration ✓' : 'Narration —'} • {data.storyboard.hasMusic ? 'Music ✓' : 'Music —'}
                  </div>
                </div>
              </div>

              {/* Package */}
              <div>
                <div className="font-medium">Package</div>
                <div className="text-slate-500 dark:text-slate-400">
                  {data.pack.ready ? `Ready${data.pack.durationMs ? ` • ${(data.pack.durationMs/1000).toFixed(0)}s` : ''}` : 'Not built'}
                </div>
              </div>

              {/* YouTube */}
              <div>
                <div className="font-medium">YouTube</div>
                <div className="text-slate-500 dark:text-slate-400">
                  {data.youtube.bound ? `Linked (${data.youtube.videoId})` : 'Not linked'}
                  {data.youtube.scheduledAt ? ` • scheduled ${new Date(data.youtube.scheduledAt).toLocaleString()}` : ''}
                  {data.youtube.publishedAt ? ` • live ${new Date(data.youtube.publishedAt).toLocaleString()}` : ''}
                </div>
              </div>

              <div className="pt-1 text-[10px] text-slate-400 dark:text-slate-500">
                Updated {new Date(data.updatedAt).toLocaleTimeString()}
              </div>
            </>
          )}
          <Tooltip.Arrow className="fill-white dark:fill-slate-800" />
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
